/**
 * Collecting and cancelling a paid integration order on a patient's chart —
 * one implementation for the Labs tab (SiPhox, TruDiagnostic, mescreen,
 * Genova, GetLabs) and the Scans/Other BodySpec section, so a fix to the
 * payment flow (as SCRUM-1401 was) reaches every vendor at once.
 *
 * - `startCharge`: open the charge modal for order(s) just placed.
 * - `chargeExisting`: the "Finish payment" row action on an order parked in
 *   awaiting_payment.
 * - `cancelExisting`: the row's Cancel; the server re-checks cancellability
 *   and reports whether money was already taken.
 * - `chargeModal`: render it once; on save every order in the charge is
 *   marked paid with the payment attached.
 */
import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  cancelIntegrationOrder,
  markIntegrationOrderPaid,
  type EnabledIntegration,
  type IntegrationOrder,
} from '@/services/integrations.service';
import { toast } from '@/store/toastStore';
import { getErrorMessage } from '@/services/apiClient';
import { ChargePaymentModal } from '@/pages/payments/ChargePaymentModal';

export interface ChargeOrder {
  orderId: number;
  amount: number;
  /** The test/exam name, shown after the product on the payment line. */
  label?: string;
}

interface ChargeCtx {
  integration: EnabledIntegration;
  integrationProductId?: number | null;
  orders: ChargeOrder[];
}

/** The orders table's query key for one integration on this chart. */
export const integrationOrdersKey = (integrationKey: string, patientId: number) =>
  [`patient-${integrationKey}-orders`, patientId] as const;

export function useIntegrationOrderCharge(patientId: number) {
  const queryClient = useQueryClient();
  const [chargeCtx, setChargeCtx] = useState<ChargeCtx | null>(null);

  const refresh = (integrationKey: string) =>
    void queryClient.invalidateQueries({ queryKey: integrationOrdersKey(integrationKey, patientId) });

  function startCharge(integration: EnabledIntegration, orders: ChargeOrder[], integrationProductId?: number | null) {
    if (orders.length > 0) setChargeCtx({ integration, integrationProductId, orders });
  }

  function chargeExisting(integration: EnabledIntegration, order: IntegrationOrder) {
    // The product actually ordered, so the modal prices and names the right
    // kit rather than the integration's first catalog item.
    const meta = order.metadata as Record<string, unknown> | undefined;
    const line = (meta?.lineItems as Array<{ integrationProductId?: number }> | undefined)?.[0];
    // Live-catalog integrations (Genova, TruDiagnostic) bill through ONE
    // generic per-account catalog product and carry the test name in metadata
    // instead — same fallback the orders table uses. Without it the payment
    // line shows only the generic integration name, never the test.
    const realName = (meta?.genovaProductName ?? meta?.skuName) as string | undefined;
    setChargeCtx({
      integration,
      integrationProductId: line?.integrationProductId ?? null,
      orders: [{ orderId: order.integrationOrderId, amount: order.amount, label: realName || undefined }],
    });
  }

  /** `noun` names the thing in the toast: "Order cancelled." / "Scan cancelled." */
  async function cancelExisting(integration: EnabledIntegration, order: IntegrationOrder, noun = 'Order') {
    try {
      const res = await cancelIntegrationOrder(patientId, order.integrationOrderId, integration.key);
      toast.success(
        res.refundNeeded
          ? `${noun} cancelled. A payment was already collected — issue a refund from Payments.`
          : `${noun} cancelled.`,
      );
      refresh(integration.key);
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  }

  // Prefill: the amount the backend computed for each order; the name and
  // catalog item of the exact product ordered, else the first product.
  const chargeProduct = chargeCtx
    ? (chargeCtx.integrationProductId != null
        ? chargeCtx.integration.products.find((p) => p.integrationProductId === chargeCtx.integrationProductId)
        : null) ?? chargeCtx.integration.products[0]
    : undefined;

  const chargeModal: ReactNode = chargeCtx !== null ? (
    <ChargePaymentModal
      open
      patientId={patientId}
      prefillItems={chargeProduct ? chargeCtx.orders.map((o) => ({
        quoteItemCostId: chargeProduct.quoteItemCostId,
        quantity: 1,
        unitPrice: o.amount,
        productTypeName: o.label ? `${chargeProduct.name} — ${o.label}` : chargeProduct.name,
      })) : undefined}
      onClose={() => setChargeCtx(null)}
      onSaved={(paymentId) => {
        const ctx = chargeCtx;
        // Every order in this charge is paid (Genova multi-test = one payment
        // for several orders). The payment id rides along so it is attached
        // rather than left for the next order (SCRUM-1401); a payment can be
        // claimed once, so the first order takes it and the rest record paid.
        for (const o of ctx.orders) {
          void markIntegrationOrderPaid(patientId, o.orderId, { collectedPaymentID: paymentId });
        }
        setChargeCtx(null);
        refresh(ctx.integration.key);
      }}
    />
  ) : null;

  return { startCharge, chargeExisting, cancelExisting, chargeModal };
}
